import { Component, ElementRef, Input, OnInit, SimpleChanges, ViewChild } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { BlobStorageService } from '../../blob-storage/blob-storage.service';
import { OrderService } from '../../orders/order.service';
import { ToasterService } from '../../toaster/toaster.service';
import { OrderPodData } from '../../orders/models/OrderPodData';
import { JsonPipe } from '@angular/common';
import { BrandingLogoDisplayComponent } from '../../branding/branding-logo-display/branding-logo-display.component';
import { GoogleMapsService } from '../../google-maps/google-maps.service';
import { ProofOfDeliveryPdfService } from '../../proof-of-delivery/proof-of-delivery-pdf.service';
import { HttpErrorResponse } from '@angular/common/http';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FormatString } from '../../constant/format-string';
import { EmailsArrayValidator } from '../../validators/email-array.validator';
import { NgSelectModule } from '@ng-select/ng-select';

@Component({
  selector: 'app-order-preview-pod-modal',
  standalone: true,
  imports: [JsonPipe, BrandingLogoDisplayComponent, NgSelectModule, ReactiveFormsModule],
  templateUrl: './order-preview-pod-modal.component.html',
  styleUrl: './order-preview-pod-modal.component.scss'
})
export class OrderPreviewPodModalComponent implements OnInit {

  @Input()
  orderId: number | null = null;

  @Input()
  deliveryStatus: string | null = null;

  sendEmailForm: FormGroup | undefined = undefined;;
  emailFormControl: FormControl;
  emailPattern = FormatString.emailPattern;

  isLoading: boolean = false;
  isPodImageLoading: boolean = false;
  orderPodData: OrderPodData | null = null;
  orderImagesString: { base64: string; content: Uint8Array; contentType: string; fileName: string, href: string | null }[] = [];

  @ViewChild('deliverylocationmap') deliveryLocationMap!: ElementRef;

  constructor(protected activeModal: NgbActiveModal,
    private orderService: OrderService,
    private toasterService: ToasterService,
    private googleMapsService: GoogleMapsService,
    private proofOfDeliveryPdfService: ProofOfDeliveryPdfService,
    private blobStorageService: BlobStorageService) {
    this.emailFormControl = new FormControl([], EmailsArrayValidator);

  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['orderId']) {
      this.orderId = changes['orderId'].currentValue
      this.loadOrderDetails();
    }
  }

  ngOnInit() {
    this.sendEmailForm = new FormGroup({
      recipients: this.emailFormControl,
    })
    this.loadOrderDetails();


  }

  loadOrderDetails() {
    this.isLoading = true;
    this.isPodImageLoading = true;
    if (!this.orderId) {
      this.toasterService.show({
        text: 'No order id provided',
        classname: 'bg-danger text-light',
        delay: 5000
      });
      this.activeModal.dismiss();
    } else {
      this.orderService.getOrderPodMetaData(this.orderId).subscribe(order => {
        this.orderPodData = order;
        this.displayGoogleMap(this.orderPodData.deliveryLocation);
        this.isLoading = false;

        this.orderService.getOrderPodImages(this.orderId!).subscribe({
          next: (images) => {
            if (this.orderPodData) {
              this.orderPodData.images = images;
            }
            this.createOrderImagesString(images);
            this.isPodImageLoading = false;
          },
          error:(errorResponse: HttpErrorResponse) => {
            this.isPodImageLoading = false;
          }
        });
      });
    }
  }

  addTag = (email: string) => {
    if (this.emailPattern.test(email)) {
        return email; // Only return the email if it's valid
    }
    return null;
  };

  sendEmail() {
    if (!this.orderId) {
      this.toasterService.show({
        text: 'No order id provided',
        classname: 'bg-danger text-light',
        delay: 5000
      });
    } else if (this.emailFormControl.valid) {
        this.proofOfDeliveryPdfService.sendOrderPodEmail(this.orderId, this.emailFormControl.value).subscribe({
            next: (response) => {
                this.emailFormControl.setValue([]);
                this.toasterService.show({
                  text: 'Emails Sent!',
                  classname: 'bg-success text-light',
                  delay: 5000
                });
            },
            error: (response : HttpErrorResponse) => {
              this.toasterService.showDanger('An error occurred: ' + response.error.message);
            }
        })
    }
}

  sendEmailPod() {
    if (!this.orderId) {
      this.toasterService.show({
        text: 'No order id provided',
        classname: 'bg-danger text-light',
        delay: 5000
      });
    } else {
      this.proofOfDeliveryPdfService.printOrderPodPdf(this.orderId).subscribe({
        next: (response) => {
          if (response) {
            this.blobStorageService.downloadFile(response.fileBytes, response.fileName);
          }
        },
        error: (response: HttpErrorResponse) => {
          console.error(response.message, 'An error occurred:');
        }
      });
    }
  }

  async displayGoogleMap(deliveryLocation: any) {
    if (!deliveryLocation) {
      return;
    }

    const loader = this.googleMapsService.getLoader();
    try {

      // We would need to find the better way to render the google map without setTimeout in the future.
      setTimeout(async () => {
        const { Map } = await loader.importLibrary('maps') as google.maps.MapsLibrary;
        const { AdvancedMarkerElement } = await loader.importLibrary('marker');

        const mapElement = this.deliveryLocationMap.nativeElement as HTMLElement;
        if (!mapElement) {
          console.error("Map element not found");
          return;
        }

        // Create the map
        const map = new Map(mapElement, {
          center: { lat: deliveryLocation.latitude, lng: deliveryLocation.longitude },
          zoom: 16,
          mapId: "delivery-location-map"
        });

        // Create the marker
        new AdvancedMarkerElement({
          map,
          position: { lat: deliveryLocation.latitude, lng: deliveryLocation.longitude }
        });
      }, 100);
    } catch (error) {
      console.error("Error loading Google Maps libraries or displaying map:", error);
    }
  }

  createOrderImagesString(images: { contentType: string; content: Uint8Array; fileName: string }[]) {
    if(images){
      this.orderImagesString = images
      .map((image) => {
        if (image.content) {
          let result : any = {
            base64: `data:${image.contentType};base64,${image.content}`,
            content: image.content,
            contentType: image.contentType,
            fileName: image.fileName,
            href: ''
          };

          result.href =  this.openImage(result.base64);
          return result;
        }
        return null; // Return null for invalid entries
      })
      .filter((img) => img !== null) as { base64: string; content: Uint8Array; contentType: string; fileName: string, href: string | null }[];
    }
  }

  openImage(base64Image: string) {
    return this.blobStorageService.createImagePreviewBlobUrl(base64Image);
  }

  downloadPodClick() {
    if (!this.orderId) {
      this.toasterService.show({
        text: 'No order id provided',
        classname: 'bg-danger text-light',
        delay: 5000
      });
    } else {
      this.proofOfDeliveryPdfService.printOrderPodPdf(this.orderId).subscribe({
        next: (response) => {
          if (response) {
            this.blobStorageService.downloadFile(response.fileBytes, response.fileName);
          }
        },
        error: (response: HttpErrorResponse) => {
          console.error(response.message, 'An error occurred:');
        }
      });
    }
  }
}
